import { type ReactNode, useState } from 'react';

import type { App, UpdateAppInput } from '@keboola/api-client/developerPortal/types';
import {
  Alert,
  Button,
  Card,
  CopyField,
  FormGroup,
  Label,
  Markdown,
  MultiComboboxField,
  setSubmitError,
  Tabs,
  TabsContent,
  toast,
  useAppForm,
} from '@keboola/design';
// Editors subpath: CodeMirror (~170 kB) is not in `@keboola/design`'s root entry.
import { CodeEditorField } from '@keboola/design/editors';

import { SaveButton } from '../../../components/SaveButton';
import { selectSaveButtonState } from '../../../components/saveButtonState';
import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { AppIcon } from '../components/AppIcon';

import { IconUploadModal } from './IconUploadModal';
import { MarkdownHelp } from './MarkdownHelp';
import {
  ALLOWED_PROCESSOR_POSITION_OPTIONS,
  CATEGORY_ITEMS,
  COMPLEXITY_OPTIONS,
  DATA_TYPE_SUPPORT_OPTIONS,
  DEFAULT_BUCKET_STAGE_OPTIONS,
  FEATURE_ITEMS,
  LOGGER_OPTIONS,
  REPOSITORY_TYPE_OPTIONS,
  STAGING_STORAGE_INPUT_OPTIONS,
  STAGING_STORAGE_OUTPUT_OPTIONS,
  TYPE_OPTIONS,
  uiOptionItems,
} from './options';
import { editAppSchemaFor } from './schema';
import {
  type Audience,
  type EditAppFormValues,
  hasConfigurationFields,
  toFormValues,
  toUpdatePayload,
} from './values';

const FORM_ID = 'edit-app';

const DOCS = {
  defaultBucket: 'https://developers.keboola.com/extend/common-interface/folders/#default-bucket',
  uiOptions: 'https://developers.keboola.com/extend/component/ui-options/',
  configurationSchema: 'https://developers.keboola.com/extend/registration/configuration-schema/',
  configurationRows:
    'https://developers.keboola.com/integrate/storage/api/configurations/#configuration-rows',
  defaultConfiguration:
    'https://developers.keboola.com/extend/component/ui-options/default-configuration/',
  actions: 'https://developers.keboola.com/extend/common-interface/actions/',
  folders: 'https://developers.keboola.com/extend/common-interface/folders/',
  gelf: 'https://developers.keboola.com/extend/common-interface/logging/#gelf-logger',
  deployScript: 'https://developers.keboola.com/extend/component/deployment/',
} as const;

/** Underlined: an inline link must not rely on colour alone (WCAG 1.4.1). */
const DocLink = ({ href, children }: { href: string; children: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="tw:text-primary tw:underline">
    {children}
  </a>
);

/**
 * ⚠️ Explicit colour: `Badge`'s default `gray` is 3.72:1 (below AA); `gray-inverse` is 5.90:1.
 */
const chipColor = () => 'gray-inverse' as const;

/**
 * How tall the two markdown editors open, in rows.
 *
 * - The only way to size them: autosize writes an inline `height !important` over any `h-*` class.
 * - Above design's default of 4 for README-length text; bounded so a 64 kB text buries nothing.
 */
const MARKDOWN_ROWS = {
  long: { minRows: 12, maxRows: 24 },
  short: { minRows: 4, maxRows: 12 },
} as const;

const SECRETS_WARNING =
  'Do not put unencrypted secrets (tokens, certificates, passwords) here. Contact support if you need help encrypting a value.';

type Props = {
  app: App;
  /** Which fields exist and which are writable. See `Audience`. */
  audience: Audience;
  /** Persists the payload; the route picks the audience's endpoint. */
  onSave: (input: UpdateAppInput) => Promise<unknown>;
  /** Where to go after a successful save — the component detail page for this audience. */
  onSaved: () => void;
};

/**
 * The component edit form, shared by the vendor dashboard and the admin panel.
 *
 * - ⚠️ Guards read values via `form.Subscribe`; `form.state.values` is a stale render snapshot.
 * - `data-app` hides `DATA_APP_HIDDEN_FIELDS` via `hasConfigurationFields`.
 * - `defaultBucket` reveals `defaultBucketStage`; `logger: 'gelf'` reveals `loggerConfiguration`.
 * - A vendor sees `requiredMemory` / `processTimeout` read-only.
 */
export function EditAppForm({ app, audience, onSave, onSaved }: Props) {
  const isAdmin = audience === 'admin';
  const [showAdvancedRepository, setShowAdvancedRepository] = useState(
    // Open when either field has a value.
    // ⚠️ Check emptiness: a missing repository arrives as `{ type: null, uri: null }`.
    (app.repository?.type ?? '') !== '' || (app.repository?.uri ?? '') !== '',
  );

  const form = useAppForm({
    defaultValues: toFormValues(app),
    // Rebuilt per validation: the schema depends on `type`, which the user can change.
    validators: formValidators((value: EditAppFormValues) =>
      editAppSchemaFor(audience, value.type),
    ),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await onSave(toUpdatePayload(value, audience));
        toast.success('The component has been updated. It may take a few minutes to propagate.');
        onSaved();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not save the component.'));
      }
    },
  });

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <Card>
        <Card.Content className="tw:flex tw:flex-col tw:gap-4 tw:sm:flex-row tw:sm:items-start">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
            <AppIcon app={app} size={64} />
            <IconUploadModal app={app} />
          </div>
          <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
            {/* Read-only: the id is what every configuration in every project references. */}
            <CopyField.Root value={app.id} mono>
              <CopyField.Label>Component ID</CopyField.Label>
              <CopyField.Box>
                <CopyField.Children>{app.id}</CopyField.Children>
                <CopyField.Button />
              </CopyField.Box>
              <CopyField.Footer>Assigned at registration and not editable.</CopyField.Footer>
            </CopyField.Root>
          </div>
        </Card.Content>
      </Card>

      <form.AppForm>
        <form.FormBody id={FORM_ID}>
          <Card>
            <Card.Header>
              <Card.Title as="h2">Basics</Card.Title>
            </Card.Header>
            <Card.Content className="tw:flex tw:flex-col tw:gap-4">
              <form.AppField name="name">
                {(field) => (
                  <field.TextField
                    label="Name"
                    required
                    placeholder="Name without the type suffix (e.g. MySQL)"
                  />
                )}
              </form.AppField>

              <form.AppField name="type">
                {(field) => <field.SelectField label="Type" required items={TYPE_OPTIONS} />}
              </form.AppField>

              {isAdmin && (
                <>
                  <form.AppField name="categories">
                    {() => (
                      <MultiComboboxField
                        label="Categories"
                        items={CATEGORY_ITEMS}
                        itemToChipColor={chipColor}
                        placeholder="Please select"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="complexity">
                    {(field) => (
                      <field.SelectField
                        label="Complexity"
                        items={COMPLEXITY_OPTIONS}
                        placeholder="Please select"
                      />
                    )}
                  </form.AppField>
                </>
              )}

              <form.AppField name="shortDescription">
                {(field) => (
                  <field.TextareaField
                    label="Short description"
                    placeholder="One or two sentences describing your component"
                    help="Shown on the component card in the catalog."
                  />
                )}
              </form.AppField>

              <form.AppField name="licenseUrl">
                {(field) => (
                  <field.TextField
                    label="License URL"
                    placeholder="Where the end user can find licence information"
                  />
                )}
              </form.AppField>
              <form.AppField name="documentationUrl">
                {(field) => (
                  <field.TextField
                    label="Documentation URL"
                    placeholder="Where the end user can find documentation"
                  />
                )}
              </form.AppField>
              <form.AppField name="sourceCodeUrl">
                {(field) => (
                  <field.TextField
                    label="Source code URL"
                    placeholder="Where the end user can find the source"
                  />
                )}
              </form.AppField>

              {isAdmin && (
                <>
                  <form.AppField name="isPublic">
                    {(field) => <field.SwitchField>Public</field.SwitchField>}
                  </form.AppField>
                  <form.AppField name="isDeprecated">
                    {(field) => <field.SwitchField>Deprecated</field.SwitchField>}
                  </form.AppField>
                </>
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title as="h2">Repository</Card.Title>
            </Card.Header>
            <Card.Content className="tw:flex tw:flex-col tw:gap-4">
              {showAdvancedRepository ? (
                <>
                  <form.AppField name="repository.type">
                    {(field) => (
                      <field.SelectField
                        label="Type"
                        items={REPOSITORY_TYPE_OPTIONS}
                        placeholder="Please select…"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="repository.uri">
                    {(field) => (
                      <field.TextField
                        label="Image name"
                        placeholder="keboola/mysql-extractor"
                        help="Not a URL — just the [registry/]vendor/component part."
                      />
                    )}
                  </form.AppField>
                </>
              ) : (
                <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                  Using a <DocLink href={DOCS.deployScript}>deploy script</DocLink> is the
                  recommended way to set the repository type and image name.{' '}
                  <Button
                    variant="inline"
                    onClick={() => setShowAdvancedRepository(true)}
                    type="button"
                  >
                    Set them manually
                  </Button>
                  .
                </p>
              )}
              <form.AppField name="repository.tag">
                {(field) => <field.TextField label="Tag" placeholder="v1.5.3" />}
              </form.AppField>
            </Card.Content>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title as="h2">Behaviour</Card.Title>
            </Card.Header>
            <Card.Content className="tw:flex tw:flex-col tw:gap-4">
              <form.Subscribe selector={(state) => hasConfigurationFields(state.values.type)}>
                {(showsConfiguration) =>
                  showsConfiguration ? (
                    <>
                      <form.AppField name="defaultBucket">
                        {(field) => (
                          <field.SwitchField
                            description={
                              <>
                                Let Keboola pick the output bucket so the user does not have to. See
                                the <DocLink href={DOCS.defaultBucket}>documentation</DocLink>.
                              </>
                            }
                          >
                            Default bucket
                          </field.SwitchField>
                        )}
                      </form.AppField>
                      <form.Subscribe selector={(state) => state.values.defaultBucket}>
                        {(defaultBucket) =>
                          defaultBucket ? (
                            <form.AppField name="defaultBucketStage">
                              {(field) => (
                                <field.SelectField
                                  label="Default bucket stage"
                                  items={DEFAULT_BUCKET_STAGE_OPTIONS}
                                />
                              )}
                            </form.AppField>
                          ) : null
                        }
                      </form.Subscribe>
                    </>
                  ) : null
                }
              </form.Subscribe>

              <form.Subscribe selector={(state) => state.values.uiOptions}>
                {(current) => (
                  <form.AppField name="uiOptions">
                    {() => (
                      <MultiComboboxField
                        label="UI options"
                        items={uiOptionItems(current)}
                        itemToChipColor={chipColor}
                        placeholder="Select which UI helpers to use"
                        help={
                          <>
                            How the component is configured in the UI. Without any of these it
                            cannot be configured through the UI at all; <code>genericDockerUI</code>{' '}
                            is the basic one. See the{' '}
                            <DocLink href={DOCS.uiOptions}>documentation</DocLink>.
                          </>
                        }
                      />
                    )}
                  </form.AppField>
                )}
              </form.Subscribe>

              <form.AppField name="imageParameters">
                {() => (
                  <CodeEditorField
                    label="Image parameters"
                    help={SECRETS_WARNING}
                    editorClassName="tw:min-h-32"
                  />
                )}
              </form.AppField>
              <form.AppField name="stackParameters">
                {() => (
                  <CodeEditorField
                    label="Stack parameters"
                    help={SECRETS_WARNING}
                    editorClassName="tw:min-h-32"
                  />
                )}
              </form.AppField>

              <form.Subscribe selector={(state) => hasConfigurationFields(state.values.type)}>
                {(showsConfiguration) =>
                  showsConfiguration ? (
                    <>
                      <form.AppField name="configurationSchema">
                        {() => (
                          <CodeEditorField
                            label="Configuration schema"
                            editorClassName="tw:min-h-40"
                            help={
                              <>
                                Lets the user fill the configuration through generated inputs. See
                                the <DocLink href={DOCS.configurationSchema}>documentation</DocLink>
                                .
                              </>
                            }
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="configurationRowSchema">
                        {() => (
                          <CodeEditorField
                            label="Configuration row schema"
                            editorClassName="tw:min-h-40"
                            help={
                              <>
                                Use together with the <code>genericDockerUI-rows</code> UI option.
                                See the{' '}
                                <DocLink href={DOCS.configurationRows}>documentation</DocLink>.
                              </>
                            }
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="emptyConfiguration">
                        {() => (
                          <CodeEditorField
                            label="Default configuration"
                            editorClassName="tw:min-h-32"
                            help={
                              <>
                                New configurations start from this. See the{' '}
                                <DocLink href={DOCS.defaultConfiguration}>documentation</DocLink>.
                              </>
                            }
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="emptyConfigurationRow">
                        {() => (
                          <CodeEditorField
                            label="Default row configuration"
                            editorClassName="tw:min-h-32"
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="createConfigurationRowSchema">
                        {() => (
                          <CodeEditorField
                            label="Create configuration row schema"
                            editorClassName="tw:min-h-32"
                            help={
                              <>
                                A form for creating several rows at once. Requires the synchronous{' '}
                                <code>prepareRows</code> action.
                              </>
                            }
                          />
                        )}
                      </form.AppField>

                      <form.AppField name="configurationDescription">
                        {(field) => (
                          <MarkdownTabs
                            value={field.state.value}
                            hasError={field.state.meta.errors.length > 0}
                          >
                            <field.TextareaField
                              label="Configuration description"
                              placeholder="Description shown above the configuration. Markdown is supported."
                              {...MARKDOWN_ROWS.short}
                            />
                          </MarkdownTabs>
                        )}
                      </form.AppField>

                      <form.Subscribe selector={(state) => state.values.actions}>
                        {(current) => (
                          <form.AppField name="actions">
                            {() => (
                              <MultiComboboxField
                                label="Actions"
                                allowCreate
                                items={current}
                                itemToChipColor={chipColor}
                                placeholder="Supported synchronous actions"
                                help={
                                  <>
                                    Synchronous actions add functionality around a run — testing a
                                    user&apos;s credentials, say. Type a name to add one. See the{' '}
                                    <DocLink href={DOCS.actions}>documentation</DocLink>.
                                  </>
                                }
                              />
                            )}
                          </form.AppField>
                        )}
                      </form.Subscribe>

                      <form.AppField name="limits">
                        {(field) => (
                          <field.TextareaField
                            label="Service limits / reasonable usage"
                            placeholder="Description of service or usage limits"
                            help="The maximum usage you expect to support — memory, time, API quotas."
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="fees">
                        {(field) => (
                          <field.SwitchField description="Check this if using the component implies additional fees.">
                            Fees
                          </field.SwitchField>
                        )}
                      </form.AppField>

                      <form.AppField name="stagingStorageInput">
                        {(field) => (
                          <field.SelectField
                            label="Staging storage input"
                            items={STAGING_STORAGE_INPUT_OPTIONS}
                            help={
                              <>
                                Keep Local if unsure. See the{' '}
                                <DocLink href={DOCS.folders}>documentation</DocLink>.
                              </>
                            }
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="stagingStorageOutput">
                        {(field) => (
                          <field.SelectField
                            label="Staging storage output"
                            items={STAGING_STORAGE_OUTPUT_OPTIONS}
                            help={
                              <>
                                Keep Local if unsure. See the{' '}
                                <DocLink href={DOCS.folders}>documentation</DocLink>.
                              </>
                            }
                          />
                        )}
                      </form.AppField>

                      <form.AppField name="logger">
                        {(field) => (
                          <field.SelectField
                            label="Logger"
                            items={LOGGER_OPTIONS}
                            help={
                              <>
                                Keep Standard if unsure. Gelf reveals the structured-logging
                                configuration below. See the{' '}
                                <DocLink href={DOCS.gelf}>documentation</DocLink>.
                              </>
                            }
                          />
                        )}
                      </form.AppField>
                    </>
                  ) : null
                }
              </form.Subscribe>

              {/* Outside the `data-app` guard: a data app keeps its gelf config. */}
              <form.Subscribe selector={(state) => state.values.logger}>
                {(logger) =>
                  logger === 'gelf' ? (
                    <form.AppField name="loggerConfiguration">
                      {() => (
                        <CodeEditorField
                          label="Logger configuration"
                          editorClassName="tw:min-h-32"
                        />
                      )}
                    </form.AppField>
                  ) : null
                }
              </form.Subscribe>
            </Card.Content>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title as="h2">Runtime</Card.Title>
            </Card.Header>
            <Card.Content className="tw:flex tw:flex-col tw:gap-4">
              {isAdmin ? (
                <>
                  <form.AppField name="forwardToken">
                    {(field) => <field.SwitchField>Forward token</field.SwitchField>}
                  </form.AppField>
                  <form.AppField name="forwardTokenDetails">
                    {(field) => <field.SwitchField>Forward token details</field.SwitchField>}
                  </form.AppField>
                  <form.AppField name="requiredMemory">
                    {(field) => (
                      <field.TextField
                        label="Required memory"
                        placeholder="128m"
                        help="A positive integer with a b / k / m / g suffix. Blank means the platform default."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="processTimeout">
                    {(field) => (
                      <field.TextField
                        label="Process timeout"
                        placeholder="3600"
                        help="Maximum processing time in seconds. Blank means the platform default."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="permissions">
                    {() => (
                      <CodeEditorField
                        label="Permissions"
                        editorClassName="tw:min-h-24"
                        help="A JSON array."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="features">
                    {() => (
                      <MultiComboboxField
                        label="Features"
                        items={FEATURE_ITEMS}
                        itemToChipColor={chipColor}
                        placeholder="Select which features to enable"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="dataTypeSupport">
                    {(field) => (
                      <field.SelectField
                        label="Data type support"
                        items={DATA_TYPE_SUPPORT_OPTIONS}
                        placeholder="Select how native data types are supported"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="allowedProcessorPosition">
                    {(field) => (
                      <field.SelectField
                        label="Allowed processor position"
                        items={ALLOWED_PROCESSOR_POSITION_OPTIONS}
                        placeholder="Select which processor position is allowed"
                      />
                    )}
                  </form.AppField>
                </>
              ) : (
                <ReadOnlyRuntime app={app} />
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title as="h2">Long description</Card.Title>
            </Card.Header>
            <Card.Content className="tw:grid tw:gap-6 tw:lg:grid-cols-[2fr_1fr]">
              <form.AppField name="longDescription">
                {(field) => (
                  <MarkdownTabs
                    value={field.state.value}
                    hasError={field.state.meta.errors.length > 0}
                  >
                    <field.TextareaField
                      label="Long description"
                      placeholder="Markdown description of the component"
                      {...MARKDOWN_ROWS.long}
                    />
                  </MarkdownTabs>
                )}
              </form.AppField>
              <MarkdownHelp />
            </Card.Content>
          </Card>

          <form.SubmitErrorAlert />

          <form.Subscribe selector={(state) => [state.submissionAttempts, state.isValid] as const}>
            {([attempts, isValid]) =>
              attempts > 0 && !isValid ? (
                <Alert variant="error" title="Some values are not valid">
                  Check the fields marked above — a message is shown next to each one.
                </Alert>
              ) : null
            }
          </form.Subscribe>

          <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
            <form.Subscribe selector={selectSaveButtonState}>
              {(state) => <SaveButton state={state}>Save</SaveButton>}
            </form.Subscribe>
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              It may take up to 5 minutes for changes to reach Keboola Connection.
            </p>
          </div>
        </form.FormBody>
      </form.AppForm>
    </div>
  );
}

/** `requiredMemory` / `processTimeout` for a vendor: read-only, changed via support. */
function ReadOnlyRuntime({ app }: { app: App }) {
  const rows = [
    { label: 'Required memory', value: app.requiredMemory },
    {
      label: 'Process timeout',
      value:
        app.processTimeout === undefined || app.processTimeout === null
          ? undefined
          : `${app.processTimeout} s`,
    },
  ];

  return (
    <>
      {rows.map(({ label, value }) => (
        <FormGroup key={label}>
          <Label>{label}</Label>
          <p className="tw:m-0 tw:text-sm">{value ?? 'Platform default'}</p>
        </FormGroup>
      ))}
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Contact{' '}
        <a className="tw:text-primary tw:underline" href="mailto:support@keboola.com">
          support@keboola.com
        </a>{' '}
        to change either of these.
      </p>
    </>
  );
}

const MARKDOWN_TRIGGERS = [
  { value: 'edit', title: 'Edit' },
  { value: 'preview', title: 'Preview' },
] as const;

/**
 * Frames a markdown field as an Edit / Preview tab pair.
 *
 * - A textarea, not `MarkdownEditor`: WYSIWYG rewrites hand-written markdown on load.
 * - ⚠️ Forced to Edit while `hasError`: Preview unmounts the field's error message.
 */
function MarkdownTabs({
  value,
  hasError = false,
  children,
}: {
  value: string;
  hasError?: boolean;
  children: ReactNode;
}) {
  const [tab, setTab] = useState<string>('edit');

  return (
    <Tabs
      triggers={MARKDOWN_TRIGGERS}
      compact
      value={hasError ? 'edit' : tab}
      onValueChange={setTab}
    >
      <TabsContent value="edit">{children}</TabsContent>
      <TabsContent value="preview">
        {value.trim() === '' ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Nothing to preview.</p>
        ) : (
          <Markdown rhythm="chrome">{value}</Markdown>
        )}
      </TabsContent>
    </Tabs>
  );
}
